import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./index.css";
import theme from "./theme";

import { ThemeProvider } from "@mui/material/styles";
import { StylesProvider as LegacyStylesProvider } from "@material-ui/core/styles";
import {
  StylesProvider as MuiStylesProvider,
  createGenerateClassName,
} from "@mui/styles";

/*
  Both style engines generate makeStyles class names in the SAME namespace --
  `makeStyles-<ruleName>-<counter>` -- from independent counters. 13 rule names
  are declared in both engines (label, formContainer, fieldContainer, paper,
  root, ...), so v4 and v5 routinely mint the identical class name for two
  unrelated components; whichever sheet sits later in <head> wins and one
  component silently renders with the other's styles. Mount order sets the
  counters, which is why the edit modals broke at random and why closing and
  reopening them shifted the counters and appeared to fix it.

  Seeding the v5 generator moves its names to `v5-makeStyles-...`, out of v4's
  namespace. Only @mui/styles is seeded, and only makeStyles names change:
  v5 components style themselves with emotion rather than @mui/styles, and no
  makeStyles(..., { name }) call exists here, so no global `Mui*` class is
  renamed. That matters -- ~193 hardcoded ".Mui*" selectors across the app
  target v4's global names, and seeding v4 renamed them and broke the Add
  Calendar modal (reverted in 3ae4d2e).
*/
const generateClassName = createGenerateClassName({ seed: "v5" });

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      {/*
        This app runs both MUI majors: @material-ui/core v4 (JSS) and
        @mui/material v5 (emotion). v4 emits bare global rules such as
        .MuiInputBase-root / .MuiOutlinedInput-root / .MuiFormLabel-root, and
        v5 puts those same names on its components as marker classes while its
        real styles live in .css-<hash> classes. Both are single-class
        selectors, so whichever stylesheet sits later in <head> wins.

        JSS decides that position at attach time: it inserts before an
        already-attached higher-index sheet, but otherwise appends to the end
        of <head> -- past emotion. So a v4 sheet attaching while no anchor
        sheet is mounted lands last and its rules override emotion on v5
        components.

        injectFirst pins every JSS sheet above emotion regardless of when it
        attaches, so v5 components are always styled by v5. This is a separate
        hazard from the makeStyles name collision above -- that one, not this,
        was what actually broke the edit modals. It deliberately does NOT
        rename any class, because ~193 hardcoded ".Mui*" selectors across the
        app target v4's global names.
      */}
      <LegacyStylesProvider injectFirst>
        <MuiStylesProvider generateClassName={generateClassName}>
          <ThemeProvider theme={theme}>
            <App />
          </ThemeProvider>
        </MuiStylesProvider>
      </LegacyStylesProvider>
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
